import React from "react";
import { Link, NavLink } from "react-router-dom";
import { Compass, MapPin, Map, Layers, Route, Download, ShieldCheck, Menu, X } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import LanguageSelector from "@/components/LanguageSelector";

export default function Navbar() {
  const { t } = useI18n();
  const [open, setOpen] = React.useState(false);

  const links = [
    { to: "/", label: t.nav.home, icon: <Compass className="w-4 h-4" /> },
    { to: "/places", label: t.nav.places, icon: <MapPin className="w-4 h-4" /> },
    { to: "/map", label: t.nav.map, icon: <Map className="w-4 h-4" /> },
    { to: "/maps", label: t.nav.maps, icon: <Layers className="w-4 h-4" /> },
    { to: "/itineraries", label: t.nav.itineraries, icon: <Route className="w-4 h-4" /> },
    { to: "/downloads", label: t.nav.downloads, icon: <Download className="w-4 h-4" /> },
  ];

  return (
    <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-border">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="navbar-logo">
          <div className="w-9 h-9 rounded-2xl flex items-center justify-center bg-[hsl(var(--primary-red))] text-white">
            <Compass className="w-5 h-5" />
          </div>
          <span className="font-display text-xl font-semibold tracking-tight">
            <span className="text-[hsl(var(--primary-red))]">Tour</span>
            <span className="text-[hsl(var(--primary-green))]">.</span>
            <span>AI</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map(l => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-medium transition flex items-center gap-2 ${
                  isActive
                    ? "bg-[hsl(var(--primary-green))] text-white"
                    : "text-foreground hover:bg-muted"
                }`
              }
            >
              {l.icon}
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSelector />
          <Link
            to="/admin"
            data-testid="nav-admin-link"
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm border border-border hover:bg-muted transition"
          >
            <ShieldCheck className="w-4 h-4" />
            {t.nav.admin}
          </Link>
          <button
            className="md:hidden p-2 rounded-full hover:bg-muted"
            onClick={() => setOpen(!open)}
            data-testid="navbar-mobile-toggle"
            aria-label="menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-border bg-white px-5 py-3 space-y-1">
          {links.map(l => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              onClick={() => setOpen(false)}
              data-testid={`nav-mobile-link-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `flex items-center gap-2 px-4 py-3 rounded-xl ${
                  isActive ? "bg-[hsl(var(--primary-green))] text-white" : "hover:bg-muted"
                }`
              }
            >
              {l.icon} {l.label}
            </NavLink>
          ))}
          <Link to="/admin" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-3 rounded-xl border border-border">
            <ShieldCheck className="w-4 h-4" /> {t.nav.admin}
          </Link>
        </div>
      )}
    </header>
  );
}
